import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { history } from 'umi';
import { LOGIN_PATH, redirectToLogin } from '@/utils/authRedirect';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success?: boolean;
  code?: number;
  message?: string;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

const handleUnauthorized = (tip?: string) => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('currentUser');
  if (tip) {
    message.error(tip);
  }
  const p = history.location.pathname;
  if (p !== '/user/login' && !window.location.pathname.endsWith(LOGIN_PATH)) {
    redirectToLogin(p + (history.location.search || ''));
  }
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        success,
        code,
        message: responseMessage,
        data,
        errorCode,
        errorMessage,
        showType,
      } = res as unknown as ResponseStructure;
      if ((typeof code === 'number' && code !== 200) || success === false) {
        const error: any = new Error(
          errorMessage || responseMessage || 'Request error',
        );
        error.name = 'BizError';
        error.info = {
          errorCode: code ?? errorCode,
          errorMessage: errorMessage || responseMessage,
          showType,
          data,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: async (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          if (errorCode === 401) {
            handleUnauthorized(errorMessage || '登录已过期，请重新登录');
            return;
          }
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        if (error.response?.status === 401 || error.response?.code === 401) {
          handleUnauthorized('登录已过期，请重新登录');
          return;
        }
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const token = localStorage.getItem('accessToken');
      const headers = {
        ...(config.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const url = `/api${config.url}`;
      return { ...config, headers, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 错误提示统一交给 errorThrower + errorHandler，避免重复弹窗
      return response;
    },
  ],
};
